import { Status } from '@/Enums/Enums';
import type {
    MetadataEditor,
    DetailViewGroup,
    DVGItem,
    Dictionary,
} from '@/Typings';
import type { PropertyRow } from '@/Datastructures';
import type {
    GridColumnDefinition,
    ColumnFactory,
    MessageBoxViewModel,
} from 'radpresentation.htmlclient.defaultviewextensions';
import {
    TreeUtilities,
    Utilities,
    Factory,
    RADApplication,
    NavigationService,
    GlobalConfiguration as Config,
    GridViewModel,
    LinkViewModel,
    ImageViewModel,
    LabelViewModel,
    ToolBarViewModel,
    AssociationGridViewModel,
} from 'radpresentation.htmlclient.defaultviewextensions';
import type ConnectPropertyGroupViewViewModel from '@/ViewModels/Views/ConnectPropertyGroupViewViewModel';
import DeletePropertyMessageBoxViewModel from '@/ViewModels/Controls/DeletePropertyMessageBoxViewModel';
import type CreateDynamicAssociationViewViewModel from '@/ViewModels/Views/CreateDynamicAssociationViewViewModel';

class PropertiesGridViewModel extends AssociationGridViewModel {
    // injected
    public metaData: MetadataEditor;

    // property
    public toolBar: ToolBarViewModel;
    /**
     * überschreibt den Typen von dataGrid aus der Oberklasse, da hier die Logik des GridViewModels verwendet wird
     * potentiell gefährlich, wenn eine Methode aus dem AssociationGridViewModel Properties/Methoden aus dem DataGridViewModel benötigt
     */
    public dataGrid: any;
    private propertyRowByPdoId: Dictionary<PropertyRow>;
    public rowsWithBlankName: KnockoutObservableArray<PropertyRow>;

    // by param
    private selectedDetailViewGroup: KnockoutObservable<DetailViewGroup>;
    private deletePropertyCallback: () => void;
    private addPropertyCallback: () => void;
    private createDynamicAssociationCallback: (
        name: string,
        aToBName: string,
        bToAName: string,
        targetType: string,
        cardinality_A_To_B: number,
        cardinality_B_To_A: number
    ) => void;
    private propertyGroupsData: KnockoutObservableArray<{
        key: string;
        title: string;
    }>;

    public refreshGrid() {
        this.dataGrid.data.valueHasMutated();
    }

    public addRow(row: PropertyRow) {
        this.dataGrid.data.push(row);
    }

    public removeRow(row: PropertyRow): void {
        this._removeRow(anyRow => anyRow.PDO_ID == row.PDO_ID);
    }

    public removeRowById(id: string): void {
        this._removeRow(propertyRow => propertyRow.PDO_ID == id);
    }

    public removeSelectedRow() {
        let selection = this.dataGrid.selected();
        if (!_.isEmpty(selection)) {
            this.removeRowById(selection[0]);
        }
    }

    private _removeRow(test: (row: PropertyRow) => boolean): void {
        let data = this.dataGrid.data() as Array<PropertyRow>;
        let selection = this.dataGrid.selected() as Array<string>;

        let index = _.findIndex(data, test);
        if (index === -1) {
            return;
        }

        let dataUpdate = data.filter(anyRow => !test(anyRow));
        let selectionUpdate = selection;

        if (!_.isEmpty(selection)) {
            let indexStillInRange = index < dataUpdate.length;
            let updateIsEmpty = _.isEmpty(dataUpdate);
            let newSelectionIndex = indexStillInRange
                ? index
                : updateIsEmpty
                  ? -1
                  : index - 1;
            selectionUpdate =
                newSelectionIndex !== -1
                    ? [dataUpdate[newSelectionIndex].PDO_ID]
                    : [];
        }

        this.dataGrid.data(dataUpdate);
        this.dataGrid.selected(selectionUpdate);
    }

    public hasRow(test: (row: PropertyRow) => boolean): boolean {
        return this.dataGrid.data().some(d => test(d));
    }

    public getRow(test: (row: PropertyRow) => boolean): PropertyRow {
        return Utilities.findFirst(this.dataGrid.data(), test);
    }

    public updateData(data: Array<PropertyRow>): void {
        this.dataGrid.forceUpdateData(data);
    }

    public getSelectedRowData(): Array<PropertyRow> {
        return this.dataGrid
            .selected()
            .map(selected => this.propertyRowByPdoId[selected]);
    }

    public select(newSelection: Array<string>) {
        this.dataGrid.selected(newSelection);
    }

    public onSelectionChange(callback: (selection: Array<string>) => void) {
        this.defineSubscription(
            this.dataGrid.selected,
            (selected: Array<string>) => {
                callback(selected);
            }
        );
    }

    public createLink: LinkViewModel;
    private deleteLink: LinkViewModel;
    private assignLink: LinkViewModel;
    private createDynamicAssociationLink: LinkViewModel;
    private moveUpLink: LinkViewModel;
    private moveDownLink: LinkViewModel;

    constructor(params: any) {
        super(params);
        this.selectedDetailViewGroup = params.selectedDetailViewGroup;
        this.deletePropertyCallback = params.deletePropertyCallback;
        this.createDynamicAssociationCallback =
            params.createDynamicAssociationCallback;
        this.addPropertyCallback = params.addPropertyCallback;
        this.propertyGroupsData = params.propertyGroupsData;

        this.rowsWithBlankName = ko.observableArray();

        this.create_createLink();
        this.create_deleteLink();
        this.create_assignLink();
        this.create_createDynamicAssociationLink();
        this.create_moveUpLink();
        this.create_moveDownLink();

        this.toolBar = new ToolBarViewModel({
            primary: [
                this.createLink,
                this.deleteLink,
                this.assignLink,
                this.createDynamicAssociationLink,
            ],
            secondary: [this.moveUpLink, this.moveDownLink],
        });

        this.dataGrid = new GridViewModel({
            identity: 'PDO_ID',
            columnDefinition: this.createGridColumnDefinitions(),
            parentLocator: this.locator,
            locator: 'datagrid',
            tooltipOptions: {
                show: false,
            },
        });

        DeletePropertyMessageBoxViewModel.yesCallback =
            this.deletePropertyCallback.bind(this);

        this.defineSubscription(
            this.dataGrid.selected,
            (selected: Array<string>) => {
                let isEmptySelection = _.isEmpty(selected);

                // keep info for re-assigning properties to another group in sync
                let detailViewGroup = this.selectedDetailViewGroup();
                let dvgItems = isEmptySelection
                    ? []
                    : detailViewGroup.Items.filter(anyItem =>
                          _.contains(selected, anyItem.PDO_ID)
                      );

                this.doAssignProperty['propertyPdoIds'] = isEmptySelection
                    ? []
                    : selected;
                this.doAssignProperty['sourceDvgGroup'] = detailViewGroup;
                this.doAssignProperty['dvgItems'] = dvgItems;

                // deactivate or activate buttons dependent on selection
                let relatedData = isEmptySelection
                    ? null
                    : (this.dataGrid
                          .data()
                          .filter(
                              row => row.PDO_ID == selected[0]
                          )[0] as PropertyRow);

                let allRows = (this.dataGrid as GridViewModel).data();
                let index = isEmptySelection
                    ? -1
                    : _.findIndex(allRows, e => e.PDO_ID == selected);
                let canMoveUp = index > 0;
                let canMoveDown = index >= 0 && index < allRows.length - 1;
                let canDelete =
                    selected.length == 1 &&
                    (relatedData.Dynamisch == true ||
                        this.isDynamicAssociation(relatedData));
                let canAssign =
                    !isEmptySelection; /*&& !this.hasDynamicAssocationSelected(selected) ISMS-724 */

                this.moveUpLink.isEnabled(canMoveUp);
                this.moveDownLink.isEnabled(canMoveDown);
                this.deleteLink.isEnabled(canDelete);
                this.assignLink.isEnabled(canAssign);
            }
        );

        this.defineSubscription(
            this.dataGrid.data,
            (dataUpdate: Array<PropertyRow>) => {
                this.propertyRowByPdoId = {};
                dataUpdate.forEach(
                    row => (this.propertyRowByPdoId[row.PDO_ID] = row)
                );

                this.rowsWithBlankName(
                    dataUpdate.filter((row: PropertyRow) =>
                        Utilities.isBlank(row.Name)
                    )
                );
            }
        );
    }

    private hasDynamicAssocationSelected(selected: Array<string>) {
        return this.dataGrid
            .data()
            .filter(row => _.contains(selected, row.PDO_ID))
            .some(row => this.isDynamicAssociation(row));
    }

    public isDynamicAssociation(row: PropertyRow): boolean {
        if (!this.metaData || !row) {
            return false;
        }
        let byName = key =>
            dynAsss[key].Source.Name == row.Name ||
            dynAsss[key].Target.Name == row.Name;
        let dynAsss = this.metaData.DynamicAssoziations;
        let dynAss = dynAsss[Utilities.findFirst(Object.keys(dynAsss), byName)];
        return !!dynAss;
    }

    private create_createLink(): void {
        this.createLink = new LinkViewModel({
            image: new ImageViewModel({
                source: Config.inlineImages.TOOLBAR_CREATE,
                alternativeText: new LabelViewModel({
                    ergName: 'RADEM.RADEXTENSIONMANAGER.CREATEPROPERTYHINT',
                }),
            }),
            tooltip: new LabelViewModel({
                ergName: 'RADEM.RADEXTENSIONMANAGER.CREATEPROPERTYHINT',
            }),
            isEnabled: false,
            onClick: () => {
                this.addPropertyCallback();
            },
            locator: this.locator() + '.create_prop',
        });
    }

    private create_deleteLink(): void {
        this.deleteLink = new LinkViewModel({
            image: new ImageViewModel({
                source: Config.inlineImages.TOOLBAR_DELETE,
                alternativeText: new LabelViewModel({
                    ergName: 'RADEM.RADEXTENSIONMANAGER.DELETEPROPERTYHINT',
                }),
            }),
            tooltip: new LabelViewModel({
                ergName: 'RADEM.RADEXTENSIONMANAGER.DELETEPROPERTYHINT',
            }),
            isEnabled: false,
            onClick: this.showDeletePropertyBox.bind(this),
            locator: this.locator() + '.delete_prop',
        });
    }

    private showDeletePropertyBox() {
        let test = row => row.PDO_ID == selectedPropertyPdoId;
        let type = this.metaData.Types[this.metaData.SubjectPDTypeMeta];

        let selectedPropertyPdoId = this.dataGrid.selected()[0];
        let selectedProperty = Utilities.findFirst(
            (this.dataGrid as GridViewModel).data(),
            test
        );
        let selectedPropertyName = selectedProperty.Name;
        let selectedTypeName = type.Name;
        let locator = this.locator() + '.delete_prop_box';

        RADApplication.getInstance().showMessageBox(
            new DeletePropertyMessageBoxViewModel(
                selectedPropertyName,
                selectedTypeName,
                locator
            ) as MessageBoxViewModel
        );
    }

    private create_moveUpLink() {
        this.moveUpLink = new LinkViewModel({
            image: new ImageViewModel({
                source: Config.inlineImages.MOVE_UP,
                alternativeText: new LabelViewModel({
                    ergName: 'RADEM.RADEXTENSIONMANAGER.MOVEUPHINT',
                }),
            }),
            tooltip: new LabelViewModel({
                ergName: 'RADEM.RADEXTENSIONMANAGER.MOVEUPHINT',
            }),
            isEnabled: false,
            onClick: () => this.moveProperty('up'),
            locator: this.locator() + '.move_up',
        });
    }

    private create_moveDownLink() {
        this.moveDownLink = new LinkViewModel({
            image: new ImageViewModel({
                source: Config.inlineImages.MOVE_DOWN,
                alternativeText: new LabelViewModel({
                    ergName: 'RADEM.RADEXTENSIONMANAGER.MOVEDOWNHINT',
                }),
            }),
            tooltip: new LabelViewModel({
                ergName: 'RADEM.RADEXTENSIONMANAGER.MOVEDOWNHINT',
            }),
            isEnabled: false,
            onClick: () => this.moveProperty('down'),
            locator: this.locator() + '.move_down',
        });
    }

    private create_assignLink() {
        this.assignLink = new LinkViewModel({
            image: new ImageViewModel({
                source: Config.inlineImages.TOOLBAR_SELECT,
                alternativeText: new LabelViewModel({
                    ergName: 'RADEM.RADEXTENSIONMANAGER.ASSIGNPROPERTYLINK',
                }),
            }),
            tooltip: new LabelViewModel({
                ergName: 'RADEM.RADEXTENSIONMANAGER.ASSIGNPROPERTYLINK',
            }),
            isEnabled: true,
            isVisible: true,
            onClick: () => {
                this.openAssignPropertyDialog();
            },
            locator: this.locator() + '.assign_prop',
        });
    }

    private create_createDynamicAssociationLink() {
        this.createDynamicAssociationLink = new LinkViewModel({
            image: new ImageViewModel({
                source: Config.inlineImages.ASSOCIATION_OBJ,
                alternativeText: new LabelViewModel({
                    ergName:
                        'RADEM.RADEXTENSIONMANAGER.CREATEDYNAMICASSOCIATIONLINK',
                }),
            }),
            tooltip: new LabelViewModel({
                ergName:
                    'RADEM.RADEXTENSIONMANAGER.CREATEDYNAMICASSOCIATIONLINK',
            }),
            isEnabled: true,
            isVisible: true,
            onClick: () => {
                this.openCreateDynamicAssociationDialog();
            },
            locator: this.locator() + '.create_dyn_asso',
        });
    }

    private moveProperty(direction: 'up' | 'down') {
        let selected = this.dataGrid.selected()[0];
        if (!selected) {
            return;
        }

        let allRows = (this.dataGrid as GridViewModel).data();
        let detailViewGroup = this.selectedDetailViewGroup();
        let detailViewGroupItems = detailViewGroup.Items;

        let movingDown = direction === 'down';

        let index = _.findIndex(allRows, e => e.PDO_ID == selected);
        let itemIndex = _.findIndex(
            detailViewGroupItems,
            e => e.PDO_ID == selected
        );

        let nextIndex = movingDown ? index + 1 : index - 1;
        let nextItemIndex = movingDown ? itemIndex + 1 : itemIndex - 1;

        let canMove = 0 <= nextIndex && nextIndex < allRows.length;
        if (!canMove) {
            return;
        }

        Utilities.swap(allRows, index, nextIndex);
        Utilities.swap(detailViewGroupItems, itemIndex, nextItemIndex);

        this.dataGrid.data.valueHasMutated();
        Status.setChanged(detailViewGroup);

        this.moveUpLink.isEnabled(movingDown ? true : index > 1);
        this.moveDownLink.isEnabled(
            movingDown ? index + 2 < allRows.length : true
        );
    }

    private doAssignProperty(targetDVG: string) {
        let dvgItems = this.doAssignProperty['dvgItems'] as Array<DVGItem>;
        let propertyPdoIds = this.doAssignProperty['propertyPdoIds'];
        let sourceDVG = this.doAssignProperty[
            'sourceDvgGroup'
        ] as DetailViewGroup;

        let detailViewGroups =
            this.metaData.DetailViews[this.metaData.SubjectDetailView]
                .DetailViewGroups;

        let targetPropertyGroup = Utilities.findFirst(
            TreeUtilities.getAllChildren(detailViewGroups, 'ChildNodes'),
            group =>
                Utilities.equalsIgnoreCase(group.PDO_ID.toString(), targetDVG)
        );

        this.moveDVGItems(dvgItems, sourceDVG, targetPropertyGroup);

        this.dataGrid.data(
            this.dataGrid
                .data()
                .filter(e => !_.contains(propertyPdoIds, e.PDO_ID))
        );
        this.dataGrid.selected([]);
    }

    private openAssignPropertyDialog() {
        let loadTuple = true;
        let params = {
            callback: this.doAssignProperty.bind(this),
            locator: this.locator() + '.connect_prop_group_box',
        };

        let viewFactory =
            NavigationService.getInstance().loadViewFactoryIsolated(
                'ConnectPropertyGroup',
                params,
                loadTuple
            );
        let viewModel =
            viewFactory.viewModel as ConnectPropertyGroupViewViewModel;

        let sourceDvgGroup = this.doAssignProperty[
            'sourceDvgGroup'
        ] as DetailViewGroup;
        let sourceDvgGroupPdoId = sourceDvgGroup.PDO_ID.toString();

        let asPdoIdNameTuple = (key: string, title: string) => {
            return { PDO_ID: key, Name: title };
        };
        let allChildren = TreeUtilities.getAllChildrenDepthFirst(
            this.propertyGroupsData(),
            'children'
        );

        let propertyGroupsData = allChildren
            .filter(groupData => groupData.key !== sourceDvgGroupPdoId)
            .map(groupData => asPdoIdNameTuple(groupData.key, groupData.title));

        viewModel.dataGrid.data(propertyGroupsData);
    }

    private openCreateDynamicAssociationDialog() {
        let loadTuple = true;
        let params = {
            callback: this.createDynamicAssociationCallback.bind(this),
            metaData: this.metaData,
            locator: this.locator() + '.create_dyn_asso_box',
        };

        let viewFactory =
            NavigationService.getInstance().loadViewFactoryIsolated(
                'CreateDynamicAssociation',
                params,
                loadTuple
            );

        let viewModel =
            viewFactory.viewModel as CreateDynamicAssociationViewViewModel;
        viewModel.yesCallback =
            this.createDynamicAssociationCallback.bind(this);
    }

    private createGridColumnDefinitions(): Array<GridColumnDefinition> {
        const visible = true;
        const hidden = false;

        return [
            $.extend(
                GridViewModel.createColumnDefinition(
                    hidden,
                    'RADEM.RADEXTENSIONMANAGER.COLUMN_ID',
                    'PDO_ID'
                ),
                { width: '0px' }
            ),
            $.extend(
                GridViewModel.createColumnDefinition(
                    visible,
                    'RADEM.RADEXTENSIONMANAGER.COLUMN_NAME',
                    'Name',
                    this.textColumn()
                ),
                { width: '191px' }
            ),
            $.extend(
                GridViewModel.createColumnDefinition(
                    visible,
                    'RADEM.RADEXTENSIONMANAGER.COLUMN_TYPE',
                    'Typ',
                    this.dataTypeColumn('datatype.png')
                ),
                { width: '180px' }
            ),
            $.extend(
                GridViewModel.createColumnDefinition(
                    visible,
                    'RADEM.RADEXTENSIONMANAGER.COLUMN_UIGENERATOR',
                    'UIGenerator',
                    this.dataTypeColumn('control.png')
                ),
                { width: '180px' }
            ),
            $.extend(
                GridViewModel.createColumnDefinition(
                    visible,
                    'RADEM.RADEXTENSIONMANAGER.COLUMN_DYNAMIC',
                    'Dynamisch',
                    this.checkBoxColumn,
                    'checkBoxColumn'
                ),
                { width: '80px' }
            ),
        ];
    }

    private checkBoxColumn = (raw, row) => {
        let params = {
            raw: raw,
            isEnabled: false,
        };

        return this.createFactory('CheckBoxControlColumn', params).getValue();
    };

    private dataTypeColumn(
        defaultValue: string
    ): (raw: any, row: PropertyRow) => any {
        return (raw, row: PropertyRow) => {
            let escapedRaw = Utilities.encodeXmlChars(raw);

            let params = {
                raw: escapedRaw,
                isDynamic: row.Dynamisch,
                default: defaultValue,
                tooltip: row.tooltip,
            };

            return this.createFactory('DataTypeColumn', params).getValue();
        };
    }

    private textColumn(): (raw: any, row: PropertyRow) => any {
        return (raw, row: PropertyRow) => {
            let params = {
                raw: raw,
                isDynamic: row.Dynamisch,
                isValidObs: row.isValid,
            };

            return this.createFactory(
                'ExtensionManagerTextColumn',
                params
            ).getValue();
        };
    }

    private createFactory(identifier: string, params: any): ColumnFactory {
        let fallback = null;

        return Factory.createInstance(
            identifier,
            params,
            fallback
        ) as ColumnFactory;
    }

    public moveDVGItems(
        dvgItems: Array<DVGItem>,
        sourceDVG: DetailViewGroup,
        targetDVG: DetailViewGroup
    ): void {
        if (_.isArray(dvgItems)) {
            let dvgItemsPdoIds = _.pluck(dvgItems, 'PDO_ID');
            sourceDVG.Items = sourceDVG.Items.filter(
                sourceGroupItem =>
                    !_.contains(dvgItemsPdoIds, sourceGroupItem.PDO_ID)
            );

            dvgItems.forEach(dvgItem => {
                Status.setChanged(dvgItem);
                if (dvgItem.Status !== Status.New) {
                    let isRedundant =
                        Utilities.findFirst(
                            this.metaData.RemovedDVGItems,
                            (obj: { $ref: any }) => obj.$ref == dvgItem['$id']
                        ) != null;
                    if (!isRedundant) {
                        this.metaData.RemovedDVGItems.push({
                            $ref: dvgItem['$id'],
                        });
                    }
                }

                targetDVG.Items.push(dvgItem);
            });
        }
    }
}

export default PropertiesGridViewModel;
